import React, { useEffect, useState } from 'react';

import Button from '../button/Button';
import InputField from '../inputField/InputField';
import PlaylistForm from '../playlistForm/PlaylistForm';
import Loader from '../loader/Loader';
import Modal from '../modal/Modal';

import { RESPONSE } from '../../js/_constants';
import { deviceStore, playlistStore } from '../../store';
import { useTranslation } from 'react-i18next';
import { getIri } from '../../js/fpUtils';
import {
    type Playlist,
    type Device,
    type ModuleType,
} from '../../api/types/index';
import { type DropdownOptionType } from '../multiSelectDropdown/MultiSelectDropdown';
import { type User } from '../../api/types/index';

const values = (item: { label: string; value: string }) => item.value;

type PropsTypes = {
    user: User;
    moduleTypes: ModuleType[];
    playlist: Playlist;
    devices: Device[];
    onCancel: () => void;
    onSuccess: () => void;
};

const EditPlaylist = (props: PropsTypes) => {
    const { t } = useTranslation();
    const { updatePlaylist } = playlistStore();
    const { unAssignDevice } = deviceStore();

    const [playlistModuleTypes, setPlaylistModuleTypes] = useState<
        DropdownOptionType[]
    >([]);

    const [selectedModuleTypes, setSelectedModuleTypes] = useState<any[]>([]);

    const [playlistDescription, setPlaylistDescription] = useState<string>('');

    const [playlistTitle, setPlaylistTitle] = useState<string>('');

    const [loading, setLoading] = useState<boolean>(true);

    const [isSaving, setIsSaving] = useState<boolean>(false);

    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

    const [incompatibleDevices, setIncompatibleDevices] = useState<Device[]>([]);

    const [playlistDevices, setPlaylistDevices] = useState<Device[]>([]);
 
    // Pre-selecting options based on playlist properties
    useEffect(() => {
        const { devices, moduleTypes, playlist } = props;

        const playlistDevices = devices || [];
        const playlistDeviceModuleTypes = playlistDevices.map(device =>
            device.moduleTypes.map(getIri)
        );

        const selectedModuleTypes = playlist.moduleTypes
            .map(item => ({
                label:
                    moduleTypes.find(t => t[RESPONSE.ID] === item)
                        ?.title || '',
                value: item
            }))
            .filter(moduleType => moduleType.label);

        const playlistModuleTypes = moduleTypes.map(option => ({
            ...option,
            label: option.title,
            value: option[RESPONSE.ID],
            disabled: playlistDeviceModuleTypes.includes(option[RESPONSE.ID])
        }));


        setPlaylistModuleTypes(playlistModuleTypes);
        setPlaylistDescription(playlist.description);
        setSelectedModuleTypes(selectedModuleTypes);
        setPlaylistTitle(playlist.title);
        setLoading(false);
        setPlaylistDevices(playlistDevices);
    }, []);

    useEffect(() => {
        const { moduleTypes } = props;

        const playlistModuleTypes = moduleTypes.map(option => ({
            ...option,
            label: option.title,
            value: option[RESPONSE.ID],
            // disabled: playlistDeviceModuleTypes.includes(option[RESPONSE.ID])
        }));
        setPlaylistModuleTypes(playlistModuleTypes);
    }, [props.moduleTypes]);

    const onSaveEditClick = async () => {
        const { playlist } = props;

        const options = {
            title: playlistTitle,
            description: playlistDescription,
            moduleTypes: selectedModuleTypes.map(values),
        };

        setIsSaving(true);

        const unAssignPromises = incompatibleDevices.map(entry =>
            unAssignDevice(entry[RESPONSE.ID], playlist[RESPONSE.ID])
        );

        if (unAssignPromises.length > 0) {
            await Promise.all(unAssignPromises);
        }

        await updatePlaylist(options);
        setIsSaving(false);

        if (props.onSuccess) {
            props.onSuccess();
        }
    };

    const checkPlaylistDevicesCompatibility = selectedModuleTypeIris => {
        const isIncludedInSelected = type =>
            selectedModuleTypeIris.includes(type[RESPONSE.ID]);
        const incompatibleDevices = playlistDevices.filter(
            (playlistDevice: Device) =>
                !playlistDevice.moduleTypes.every(isIncludedInSelected)
        );
        setIncompatibleDevices(incompatibleDevices);
    };

    const onOpenDeleteConfirm = () => {
        setShowConfirmation(true);
    };

    const onCloseDeleteConfirm = () => {
        setShowConfirmation(false);
    };

    const handleModuleTypeSelection = selectedOptions => {
        const selectedIris = selectedOptions.map(selected => selected.value);
        const nextSelected = [...selectedOptions];
        setSelectedModuleTypes(nextSelected);
        checkPlaylistDevicesCompatibility(selectedIris);
    };

    const handleDescriptionChange = e => {
        setPlaylistDescription(e.target.value);
    };

    const handleTitleChange = value => {
        setPlaylistTitle(value);
    };

    const { onCancel, user } = props;

    if (loading) {
        return <Loader loading={loading} />;
    }
    return (
        <div className="description">
            <div className="edit">
                <div className="inputs">
                    <div className="left-side">
                        <PlaylistForm
                            user={user}
                            title={playlistTitle}
                            moduleTypeOptions={playlistModuleTypes}
                            selectedModuleTypes={selectedModuleTypes}
                            onModuleTypeSelection={handleModuleTypeSelection}
                            onTitleChange={handleTitleChange}
                        />
                    </div>
                    <div className="right-side">
                        <InputField
                            label={t('device_detail_edit_description_title')}
                            name="description"
                            inputType="textarea"
                            onChangeHandler={handleDescriptionChange}
                            value={playlistDescription}
                        />
                    </div>
                </div>
                <div className="b-modal-action-buttons edit-buttons">
                    <Button
                        secondaryButton
                        text={t('cancel')}
                        clickHandler={onCancel}
                    />
                    <Button
                        disabled={playlistTitle.length === 0}
                        isLoading={isSaving}
                        text={t('save')}
                        clickHandler={
                            incompatibleDevices.length > 0
                                ? onOpenDeleteConfirm
                                : onSaveEditClick
                        }
                    />
                </div>
                <Modal
                    className="confirmation-modal"
                    show={showConfirmation}
                    onClose={onCloseDeleteConfirm}
                    onCancel={onCloseDeleteConfirm}
                    onAction={onSaveEditClick}
                    localeAction={t('apply')}
                    localeCancel={t('discard')}
                >
                    <h4>{t('playlist_change_module')}</h4>
                    <ul className="devices-list">
                        {incompatibleDevices.map((device: Device, index) => (
                            <li
                                className="device-title"
                                key={device.title + index}
                            >
                                {device.title}
                            </li>
                        ))}
                    </ul>
                </Modal>
            </div>
        </div>
    );
};

export default EditPlaylist;
